import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaArrowsAlt, FaImage, FaRedoAlt, FaSearchMinus, FaSearchPlus, FaUpload } from 'react-icons/fa';
import useMediaUpload from '../hooks/useMediaUpload';
import './OpeningCoverEditor.css';

const DEFAULT_CROP = {
  x: 0,
  y: 0,
  zoom: 1,
  aspect: 9 / 16,
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const normalizeCrop = (crop = {}) => ({
  x: clamp(Number(crop.x) || 0, -45, 45),
  y: clamp(Number(crop.y) || 0, -45, 45),
  zoom: clamp(Number(crop.zoom) || 1, 1, 2.6),
  aspect: Number(crop.aspect) || DEFAULT_CROP.aspect,
});

const getCoverImageUrl = (openingCover = {}) => (
  openingCover.imageUrl ||
  openingCover.url ||
  openingCover.coverImageUrl ||
  ''
);

const OpeningCoverEditor = ({
  pageData = {},
  setPageData,
  pageId,
  mediaUsage,
  theme,
}) => {
  const inputRef = useRef(null);
  const dragStartRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const {
    isUploading,
    uploadError,
    setUploadError,
    uploadAdminFile,
  } = useMediaUpload({
    pageId,
    moduleId: 'opening_cover',
    moduleType: 'opening_cover',
    mediaUsage,
  });

  const openingCover = pageData.openingCover || {};
  const imageUrl = getCoverImageUrl(openingCover) || pageData.coverImageUrl || '';
  const crop = useMemo(() => normalizeCrop(openingCover.crop), [openingCover.crop]);
  const hasImage = Boolean(imageUrl);

  const persistOpeningCover = (patch) => {
    setPageData?.((prev) => {
      const previousCover = prev.openingCover || {};
      const nextCover = {
        ...previousCover,
        crop: normalizeCrop(previousCover.crop),
        ...patch,
      };
      if (patch.crop) nextCover.crop = normalizeCrop(patch.crop);
      return {
        ...prev,
        openingCover: nextCover,
        coverImageUrl: nextCover.imageUrl || prev.coverImageUrl || '',
      };
    });
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadError('');

    const uploadedUrl = await uploadAdminFile(file, {
      mediaKind: 'image',
      storageFolder: 'images',
      replacingExisting: hasImage,
      fileName: `opening_cover_${Date.now()}_${file.name}`,
    });

    if (uploadedUrl) {
      persistOpeningCover({
        imageUrl: uploadedUrl,
        crop: DEFAULT_CROP,
        updatedAt: Date.now(),
      });
    }

    event.target.value = '';
  };

  const updateCrop = (patch) => {
    persistOpeningCover({ crop: { ...crop, ...patch } });
  };

  const resetCrop = () => {
    persistOpeningCover({ crop: DEFAULT_CROP });
  };

  const handlePointerDown = (event) => {
    if (!hasImage) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragStartRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      cropX: crop.x,
      cropY: crop.y,
    };
    setIsDragging(true);
  };

  const handlePointerMove = (event) => {
    const dragStart = dragStartRef.current;
    if (!dragStart || dragStart.pointerId !== event.pointerId) return;
    const deltaX = (event.clientX - dragStart.startX) / 3.2;
    const deltaY = (event.clientY - dragStart.startY) / 3.2;
    updateCrop({
      x: clamp(dragStart.cropX + deltaX, -45, 45),
      y: clamp(dragStart.cropY + deltaY, -45, 45),
    });
  };

  const endDrag = () => {
    dragStartRef.current = null;
    setIsDragging(false);
  };

  useEffect(() => () => {
    dragStartRef.current = null;
  }, []);

  return (
    <section className="lumirae-opening-cover-editor">
      <div className="lumirae-opening-cover-editor-header">
        <div>
          <span>Opening cover photo</span>
          <strong>Make the first screen feel real</strong>
          <p>Upload a dedicated 9:16 cover. Drag to reposition and zoom without changing the original file.</p>
        </div>
        <button
          type="button"
          className="lumirae-opening-cover-upload"
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
        >
          <FaUpload />
          {hasImage ? 'Change photo' : 'Upload photo'}
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleUpload}
        disabled={isUploading}
      />

      <div className="lumirae-opening-cover-editor-body">
        <div
          className={`lumirae-opening-cover-cropper ${hasImage ? 'has-image' : ''} ${isDragging ? 'is-dragging' : ''}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          role="img"
          aria-label="Opening cover crop preview"
        >
          {hasImage ? (
            <img
              src={imageUrl}
              alt=""
              draggable="false"
              style={{
                transform: `translate(-50%, -50%) translate(${crop.x}%, ${crop.y}%) scale(${crop.zoom})`,
              }}
            />
          ) : (
            <div className="lumirae-opening-cover-empty">
              <FaImage />
              <strong>Wedding photo, family portrait, baby moment, or memory cover</strong>
              <span>Shown before they tap Open the Gift.</span>
            </div>
          )}
          {hasImage && (
            <div className="lumirae-opening-cover-crop-hint">
              <FaArrowsAlt />
              Drag photo to frame the moment
            </div>
          )}
        </div>

        <div className="lumirae-opening-cover-controls">
          <label>
            <span>
              <FaSearchMinus />
              Zoom
              <FaSearchPlus />
            </span>
            <input
              type="range"
              min="1"
              max="2.6"
              step="0.05"
              value={crop.zoom}
              onChange={(event) => updateCrop({ zoom: Number(event.target.value) })}
              disabled={!hasImage}
              style={{ accentColor: theme?.accent }}
            />
          </label>
          <div className="lumirae-opening-cover-control-row">
            <button type="button" onClick={resetCrop} disabled={!hasImage}>
              <FaRedoAlt />
              Reset crop
            </button>
            <span>{hasImage ? 'Saved as editable crop data' : 'Theme fallback is used until you upload'}</span>
          </div>
          {uploadError && (
            <p className="lumirae-opening-cover-error">{uploadError}</p>
          )}
        </div>
      </div>
    </section>
  );
};

export default OpeningCoverEditor;
